/**
 * Instant Search Selectors Service
 *
 * Handles theme detection and search input integration.
 *
 * **Migration from Legacy**:
 * - Legacy: Hard-coded selectors in index.js, theme-specific logic scattered
 * - New: Centralized theme configuration with extensibility
 *
 * @module instant-search.selectors
 */

import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import type {
  SearchSettings,
  ThemeSearchConfig,
  ThemeSearchConfigMap,
} from './instant-search.types';
import { PositionService } from './position.service';

/** CSS selector for the ISW root element */
const ISW_ELEMENT_SELECTOR = '#bc-instant-search';

/** CSS class used to hide suggestion panel */
const HIDE_CLASS = 'boost-sd__g-hide';

/** Timeout in milliseconds for MutationObserver-based element waiting */
const OBSERVER_TIMEOUT_MS = 10_000;

/**
 * CSS selectors for Shopify predictive search containers.
 * Tried in priority order when looking inside a dialog element.
 */
const PREDICTIVE_SEARCH_SELECTORS = [
  'predictive-search-component',
  'predictive-search',
  '.predictive-search',
  '.search-modal__content',
] as const;

/**
 * Default close-button selectors for known themes that use overlays.
 * Used when ISW opens in full-width style to dismiss the theme's own search UI.
 *
 * In order: Prestige, Super Store, Venture/District.
 */
const DEFAULT_CLOSE_SELECTORS = [
  '#Search .Search__Close',
  '.js-drawer-open .page-container',
  '.menu-drawer__modal-close.modal__close',
] as const;

/**
 * Service for theme detection and selector management.
 *
 * Technical Support teams can extend this service for custom themes:
 *
 * @example
 * ```javascript
 * window.boostWidgetIntegration.extend('InstantSearchSelectors', (InstantSearchSelectors) => {
 *   return class CustomSelectors extends InstantSearchSelectors {
 *     // Add custom theme detection
 *     protected getThemeConfigs() {
 *       return {
 *         ...super.getThemeConfigs(),
 *         myCustomTheme: {
 *           searchInputSelector: '.my-search-input',
 *           dialogSelector: '.my-search-modal'
 *         }
 *       };
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class InstantSearchSelectors {
  constructor(
    protected appService: AppService,
    protected positionService: PositionService
  ) {}

  /**
   * Get theme configurations map.
   * Override to add custom theme support.
   *
   * **Legacy**: Hard-coded selectors scattered across index.js
   * **New**: Centralized configuration object
   */
  protected getThemeConfigs(): ThemeSearchConfigMap {
    return {
      // Shopify Free Themes
      dawn: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: 'dialog.search-modal__content, #search-modal dialog',
        closeButtonSelector: '.search-modal__close-button',
        theme: 'dawn',
      },
      debut: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-modal, .drawer--is-open',
        closeButtonSelector: '.search-modal__close, .drawer__close',
        theme: 'debut',
      },
      brooklyn: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-bar--open',
        closeButtonSelector: '.search-bar__close',
        theme: 'brooklyn',
      },
      narrative: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-drawer--is-open',
        closeButtonSelector: '.search-drawer__close',
        theme: 'narrative',
      },
      supply: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-form--active',
        closeButtonSelector: '.search-form__close',
        theme: 'supply',
      },
      minimal: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-bar--active',
        closeButtonSelector: '.search-bar__close',
        theme: 'minimal',
      },
      simple: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search--open',
        closeButtonSelector: '.search__close',
        theme: 'simple',
      },
      boundless: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-active',
        closeButtonSelector: '.search__close',
        theme: 'boundless',
      },
      venture: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-bar--active',
        closeButtonSelector: '.search-bar__close',
        theme: 'venture',
      },

      // Premium Themes
      prestige: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '#Search, .search-drawer',
        closeButtonSelector: '.Search__Close, .search-drawer__close',
        theme: 'prestige',
      },
      impulse: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.js-drawer-open, .drawer--search',
        closeButtonSelector: '.drawer__close',
        theme: 'impulse',
      },
      symmetry: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.show-search, .search-modal--active',
        closeButtonSelector: '.search-close',
        theme: 'symmetry',
      },
      warehouse: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search--is-open, .search-drawer--open',
        closeButtonSelector: '.search__close-button',
        theme: 'warehouse',
      },
      empire: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-active, .quick-search--active',
        closeButtonSelector: '.search-close',
        theme: 'empire',
      },
      turbo: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-modal--open, .search-drawer--open',
        closeButtonSelector: '.search-modal__close, .search-drawer__close',
        theme: 'turbo',
      },
      flex: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-overlay--active',
        closeButtonSelector: '.search-overlay__close',
        theme: 'flex',
      },
      motion: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-modal[open], .search-drawer[open]',
        closeButtonSelector: '.search-modal__close',
        theme: 'motion',
      },
      impact: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-modal--open',
        closeButtonSelector: '.search-modal__close',
        theme: 'impact',
      },
      craft: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search--active',
        closeButtonSelector: '.search__close',
        theme: 'craft',
      },
      sense: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: '.search-modal[open]',
        closeButtonSelector: '.search-modal__close-button',
        theme: 'sense',
      },

      // Default fallback
      default: {
        searchInputSelector: 'input[name="q"]:not([data-disable-instant-search])',
        dialogSelector: undefined,
        closeButtonSelector: undefined,
        theme: 'default',
      },
    };
  }

  /**
   * Get all search input elements.
   *
   * **Legacy**: document.querySelectorAll('input[name="q"]...')
   * **New**: Method using theme-specific selector
   *
   * @returns Array of search input elements
   */
  getAllSearchInputs(): HTMLInputElement[] {
    const config = this.getThemeSearchConfig();
    const inputs = document.querySelectorAll<HTMLInputElement>(config.searchInputSelector);
    return Array.from(inputs);
  }

  /**
   * Get theme-specific search configuration.
   *
   * Detection priority:
   * 1. Custom config from window.boostWidgetIntegrationConfig
   * 2. Theme name from window.theme or Shopify theme settings
   * 3. Selector presence detection
   * 4. Default fallback
   *
   * @returns Theme search configuration
   */
  getThemeSearchConfig(): ThemeSearchConfig {
    // 1. Check for custom config from window
    const customConfig = window.boostWidgetIntegrationConfig?.instantSearchThemes;
    if (customConfig) {
      const customTheme = this.matchThemeFromConfig(customConfig);
      if (customTheme) return customTheme;
    }

    const themeConfigs = this.getThemeConfigs();

    // 2. Check window.theme for theme name
    const themeName = this.detectThemeName();
    if (themeName) {
      const normalizedName = themeName.toLowerCase().replace(/[^a-z0-9]/g, '');
      for (const [key, config] of Object.entries(themeConfigs)) {
        if (normalizedName.includes(key)) {
          return config;
        }
      }
    }

    // 3. Try to match by selector presence
    for (const config of Object.values(themeConfigs)) {
      if (config.dialogSelector && document.querySelector(config.dialogSelector)) {
        return config;
      }
    }

    // 4. Fallback to default
    return themeConfigs.default;
  }

  /**
   * Detect theme name from various sources.
   */
  protected detectThemeName(): string | null {
    // Check window.theme
    const windowTheme = (window as unknown as { theme?: { name?: string } }).theme;
    if (windowTheme?.name) {
      return windowTheme.name;
    }

    // Check Shopify.theme
    const shopifyTheme = (window as unknown as { Shopify?: { theme?: { name?: string } } }).Shopify
      ?.theme;
    if (shopifyTheme?.name) {
      return shopifyTheme.name;
    }

    // Check meta tag
    const metaTheme = document.querySelector('meta[name="theme-name"]');
    if (metaTheme) {
      return metaTheme.getAttribute('content');
    }

    return null;
  }

  /**
   * Match theme from custom config.
   */
  protected matchThemeFromConfig(customConfig: ThemeSearchConfigMap): ThemeSearchConfig | null {
    const themeName = this.detectThemeName();

    // Try exact match first
    if (themeName) {
      const normalizedName = themeName.toLowerCase().replace(/[^a-z0-9]/g, '');
      for (const [key, config] of Object.entries(customConfig)) {
        if (normalizedName.includes(key.toLowerCase())) {
          return config;
        }
      }
    }

    // Return first config if only one is provided
    const keys = Object.keys(customConfig);
    if (keys.length === 1) {
      return customConfig[keys[0]];
    }

    return null;
  }

  /**
   * Detect open theme dialogs.
   *
   * **1:1 port of legacy `detectThemeDialogs()`.**
   * Uses the same 3 hardcoded selectors — all of which already require `[open]`
   * in the selector string, so only currently-open dialogs are returned.
   *
   * @returns Array of open dialog elements (deduplicated)
   */
  detectThemeDialogs(): HTMLElement[] {
    const dialogSelectors = [
      'dialog[open].search-modal__content',
      '#search-modal dialog[open]',
      '#search-modal [data-ref="dialog"][open]',
    ];
    const foundDialogs: HTMLElement[] = [];

    dialogSelectors.forEach((selector) => {
      const dialogs = document.querySelectorAll<HTMLElement>(selector);
      dialogs.forEach((dialog) => {
        if (dialog && !foundDialogs.includes(dialog)) {
          foundDialogs.push(dialog);
        }
      });
    });

    return foundDialogs;
  }

  /**
   * Hide theme search dialogs when ISW opens (full-width style).
   *
   * **1:1 port of legacy `hideThemeDialogWhenISWOpens()`.**
   * Closes each open dialog and marks it with `boostWasClosed` so
   * `restoreThemeDialog()` can identify which ones we closed.
   *
   * @returns True if at least one dialog was closed
   */
  hideThemeDialog(): boolean {
    const dialogs = this.detectThemeDialogs();
    if (dialogs.length === 0) return false;

    let closedSearchDialog = false;

    dialogs.forEach((dialog) => {
      const isOpen = dialog.hasAttribute('open') || (dialog as HTMLDialogElement).open === true;

      if (isOpen) {
        dialog.dataset.boostWasClosed = 'true';
        if (typeof (dialog as HTMLDialogElement).close === 'function') {
          (dialog as HTMLDialogElement).close();
        } else {
          dialog.removeAttribute('open');
        }
        closedSearchDialog = true;
      }
    });

    return closedSearchDialog;
  }

  /**
   * Restore theme dialogs when ISW closes.
   *
   * **1:1 port of legacy `restoreThemeDialogWhenISWCloses()`.**
   *
   * Note: In practice this is effectively a no-op for the full-width case.
   * `detectThemeDialogs()` requires `[open]` in its selectors.  After
   * `hideThemeDialog()` calls `dialog.close()`, `[open]` is removed, so
   * `detectThemeDialogs()` returns an empty array and the forEach never runs.
   * The dialog stays closed until the user explicitly opens the theme search again.
   *
   * For the dropdown/injection case where the dialog stayed open, the
   * `handleDialogIntegration` observer closes it via `moveISWBackToBody()` when
   * the theme itself fires the close event — not from here.
   */
  restoreThemeDialog(): void {
    const dialogs = this.detectThemeDialogs();

    if (dialogs.length === 0) {
      return;
    }

    dialogs.forEach((dialog) => {
      // Only reopen if we were the ones who closed it
      if (dialog.dataset.boostWasClosed === 'true') {
        if (typeof (dialog as HTMLDialogElement).showModal === 'function') {
          (dialog as HTMLDialogElement).showModal();
        } else if (typeof (dialog as HTMLDialogElement).show === 'function') {
          (dialog as HTMLDialogElement).show();
        } else {
          dialog.setAttribute('open', '');
        }

        // Clean up our marker
        delete dialog.dataset.boostWasClosed;
      }
    });
  }

  /**
   * Check if current style is full-width.
   *
   * **Legacy**: isStyleFullWidth(context) function
   * **New**: Method on selectors service
   *
   * @param searchSettings - Search settings
   * @returns True if full-width style is active
   */
  isFullWidthStyle(searchSettings: SearchSettings): boolean {
    return this.positionService.isStyleFullWidth(searchSettings);
  }

  /**
   * Check if current device is mobile or tablet (≤ 991 px).
   *
   * Matches the legacy `isMobile(isTabletPortraitMax)` threshold used by the
   * old ISW code, which treats tablets up to 991 px as "mobile" for layout
   * decisions (full-width style, container selector, etc.).
   *
   * @returns True if device width is below the tablet-portrait-max breakpoint
   */
  protected isMobileDevice(): boolean {
    return this.appService.detectDeviceByWidth().includes('tablet_portrait_max');
  }

  /**
   * Get ISW container selector.
   *
   * @param isMobile - Whether mobile mode
   * @returns CSS selector for ISW container
   */
  getContainerSelector(isMobile: boolean): string {
    return `.boost-sd__instant-search-container-${isMobile ? 'mobile' : 'desktop'}`;
  }

  /**
   * Get ISW wrapper selector.
   *
   * @param isMobile - Whether mobile mode
   * @returns CSS selector for ISW wrapper
   */
  getWrapperSelector(isMobile: boolean): string {
    return `${this.getContainerSelector(isMobile)} .boost-sd__search-widget-init-wrapper`;
  }

  // ===========================================================================
  // Element Waiting Methods
  // ===========================================================================

  /**
   * Wait for the ISW root element (`#bc-instant-search`) to appear in the DOM.
   *
   * Uses a MutationObserver on `document.body` so the caller can `await` the
   * element even if it is injected asynchronously by the template renderer.
   *
   * **Legacy**: `waitForInstantSearchElement()` standalone function
   * **New**: Instance method on selectors service
   *
   * @param timeout - Maximum wait time in ms (default 10 000). Pass `0` to wait indefinitely.
   * @returns The ISW root element, or `null` if the timeout is reached
   */
  waitForISWElement(timeout: number = OBSERVER_TIMEOUT_MS): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const existing = document.querySelector<HTMLElement>(ISW_ELEMENT_SELECTOR);
      if (existing) {
        resolve(existing);
        return;
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const observer = new MutationObserver((_mutations, obs) => {
        const element = document.querySelector<HTMLElement>(ISW_ELEMENT_SELECTOR);
        if (element) {
          obs.disconnect();
          if (timeoutId) clearTimeout(timeoutId);
          resolve(element);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      if (timeout > 0) {
        timeoutId = setTimeout(() => {
          observer.disconnect();
          resolve(null);
        }, timeout);
      }
    });
  }

  /**
   * Wait for a predictive search container inside a dialog element.
   *
   * Shopify themes render predictive search components asynchronously inside
   * their search dialogs. This method waits for one of the known selectors
   * to become available so ISW can inject itself adjacent to the container.
   *
   * **Legacy**: `waitForPredictiveSearchContainer(dialog)` standalone function
   * **New**: Instance method on selectors service with timeout support
   *
   * @param dialog - The dialog / modal element to search within
   * @param timeout - Maximum wait time in ms (default 10 000). Pass `0` to wait indefinitely.
   * @returns The predictive search container, or `null` if not found within timeout
   */
  waitForPredictiveSearchContainer(
    dialog: HTMLElement,
    timeout: number = OBSERVER_TIMEOUT_MS
  ): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      // Try to find immediately
      const found = this.findPredictiveSearchContainer(dialog);
      if (found) {
        resolve(found);
        return;
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      const observer = new MutationObserver((_mutations, obs) => {
        const container = this.findPredictiveSearchContainer(dialog);
        if (container) {
          obs.disconnect();
          if (timeoutId) clearTimeout(timeoutId);
          resolve(container);
        }
      });

      observer.observe(dialog, { childList: true, subtree: true });

      if (timeout > 0) {
        timeoutId = setTimeout(() => {
          observer.disconnect();
          resolve(null);
        }, timeout);
      }
    });
  }

  /**
   * Find a predictive search container inside a parent element.
   *
   * Tries each selector from `PREDICTIVE_SEARCH_SELECTORS` in priority order.
   *
   * @param parent - Parent element to search within
   * @returns The first matching element, or `null`
   */
  protected findPredictiveSearchContainer(parent: HTMLElement): HTMLElement | null {
    for (const selector of PREDICTIVE_SEARCH_SELECTORS) {
      const container = parent.querySelector<HTMLElement>(selector);
      if (container) return container;
    }
    return null;
  }

  // ===========================================================================
  // Visibility Helpers
  // ===========================================================================

  /**
   * Check whether the suggestion panel is currently visible.
   *
   * The panel is considered visible when its wrapper element exists and does
   * **not** have the `boost-sd__g-hide` CSS class.
   *
   * **Legacy**: `isSuggestionOpening(suggestionSelector)`
   * **New**: Method that resolves the wrapper internally
   *
   * @returns `true` if the suggestion panel is visible
   */
  isSuggestionVisible(): boolean {
    const isMobile = this.isMobileDevice();
    const selector = `${this.getContainerSelector(isMobile)} > div`;
    const wrapper = document.querySelector<HTMLElement>(selector);
    return wrapper !== null && !wrapper.classList.contains(HIDE_CLASS);
  }

  /**
   * Get the suggestion wrapper element for the current device.
   *
   * @returns The suggestion wrapper element, or `null`
   */
  getSuggestionWrapper(): HTMLElement | null {
    const isMobile = this.isMobileDevice();
    const selector = `${this.getContainerSelector(isMobile)} > div`;
    return document.querySelector<HTMLElement>(selector);
  }

  // ===========================================================================
  // Theme Close-Button Integration
  // ===========================================================================

  /**
   * Build the combined CSS selector for theme close buttons.
   *
   * Merges the built-in close-button selectors (Prestige, Super Store, etc.)
   * with an optional custom selector from `generalSettings.closeSearchThemeSelector`.
   *
   * @param customSelector - Optional custom selector from admin settings
   * @returns Combined CSS selector string
   */
  protected getCloseSearchThemeSelector(customSelector?: string): string {
    const selectors: string[] = [...DEFAULT_CLOSE_SELECTORS];

    if (customSelector) {
      selectors.push(customSelector);
    }

    return selectors.join(',');
  }

  /**
   * Click all visible theme close buttons to dismiss overlays.
   *
   * When ISW opens in **full-width** style, certain themes show their own
   * search overlay/modal that conflicts with ISW. This method clicks all
   * known close buttons to dismiss those overlays.
   *
   * **Legacy**: Inline logic inside `onOpenSuggestion()` for full-width style
   * **New**: Dedicated method on selectors service
   *
   * @param customSelector - Optional custom close-button selector from admin settings
   */
  closeThemeSearchOverlays(customSelector?: string): void {
    const selector = this.getCloseSearchThemeSelector(customSelector);

    const closeButtons = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null
    );

    if (closeButtons.length > 0) {
      requestAnimationFrame(() => {
        closeButtons.forEach((button) => button.click());
      });
    }
  }

  // ===========================================================================
  // Symmetry Theme Helpers
  // ===========================================================================

  /**
   * Check whether the Symmetry theme's search mode is active.
   *
   * Symmetry adds a `show-search` class to `document.body` when its search
   * overlay opens. When ISW closes it must remove this class first and delay
   * the actual hide by 200 ms so the theme's CSS transition completes.
   *
   * **Legacy**: `document.body.classList.contains('show-search')` inline check
   * **New**: Dedicated predicate method
   *
   * @returns `true` if the Symmetry search overlay is open
   */
  isSymmetrySearchOpen(): boolean {
    return document.body.classList.contains('show-search');
  }

  /**
   * Dismiss the Symmetry theme's search overlay.
   *
   * Removes the `show-search` class from `document.body`.
   *
   * @returns `true` if the class was present and removed
   */
  dismissSymmetrySearch(): boolean {
    if (!this.isSymmetrySearchOpen()) return false;
    document.body.classList.remove('show-search');
    return true;
  }

  // ===========================================================================
  // ISW Element Selector
  // ===========================================================================

  /**
   * Get the ISW root element selector.
   *
   * @returns CSS selector string for the ISW root element
   */
  getISWElementSelector(): string {
    return ISW_ELEMENT_SELECTOR;
  }

  /**
   * Get the ISW root element from the DOM.
   *
   * @returns The ISW root element, or `null` if not present
   */
  getISWElement(): HTMLElement | null {
    return document.querySelector<HTMLElement>(ISW_ELEMENT_SELECTOR);
  }

  /**
   * Get the predictive search selectors list.
   *
   * Useful for extending or customizing which selectors are tried.
   *
   * @returns Array of CSS selector strings
   */
  getPredictiveSearchSelectors(): readonly string[] {
    return PREDICTIVE_SEARCH_SELECTORS;
  }
}
